import { FunctionComponent, useState } from 'react'
import classNames from 'classnames'
import { Icon } from '../../shared/Icon'
import { DictationButtonState, ModeId, ModeConfig } from './DictationWidget.types'
import { ModeSelector } from './ModeSelector.component'
import { WidgetPopover } from './WidgetPopover.component'
import styles from './DictationWidget.module.scss'

// ── Props ─────────────────────────────────────────────────────────
type Props = {
  mode: ModeId
  activeMode: ModeConfig
  availableModes: ModeConfig[]
  activeMic: MediaDeviceInfo | undefined
  devices: MediaDeviceInfo[]
  selectedDeviceId: string | undefined
  modeOpen: boolean
  micOpen: boolean
  isDisabled: boolean
  isRecording: boolean
  buttonState: DictationButtonState
  panelDirection: 'down' | 'up'
  isPanelOpen: boolean
  onMouseDown: (e: React.MouseEvent) => void
  onRecordToggle: () => void
  onDropdownToggle: (which: 'mode' | 'mic') => void
  onModeChange: (id: ModeId) => void
  onMicSelect: (deviceId: string) => void
  onSettingsClick: () => void
  setModeOpen: (open: boolean) => void
  setMicOpen: (open: boolean) => void
}

// ── Component ─────────────────────────────────────────────────────
export const DictationBar: FunctionComponent<Props> = ({
  mode,
  activeMode,
  availableModes,
  activeMic,
  devices,
  selectedDeviceId,
  modeOpen,
  micOpen,
  isDisabled,
  isRecording,
  buttonState,
  panelDirection,
  isPanelOpen,
  onMouseDown,
  onRecordToggle,
  onDropdownToggle,
  onModeChange,
  onMicSelect,
  onSettingsClick,
  setModeOpen,
  setMicOpen,
}) => {
  const [infoOpen, setInfoOpen] = useState(false)

  return (
    <div className={styles.barZone}>
      <div
        className={classNames(styles.bar, {
          [styles.barPanelDown]: isPanelOpen && panelDirection === 'down',
          [styles.barPanelUp]: isPanelOpen && panelDirection === 'up',
        })}
        onMouseDown={onMouseDown}
      >
        {/* Record button — icon only */}
        <button
          className={styles.recBtn}
          onClick={onRecordToggle}
          disabled={isDisabled}
          aria-label={isRecording ? 'Arrêter la dictée' : 'Démarrer la dictée'}
        >
          <span className={styles.ip} data-state={buttonState} data-mode={mode}>
            <Icon icon={activeMode.iconName} size="pico" />
          </span>
        </button>

        {/* Mode label + caret grouped */}
        <div className={styles.modeBtn}>
          <button
            type="button"
            className={styles.modeLabelBtn}
            onMouseDown={(e) => e.nativeEvent.stopPropagation()}
            onClick={() => onDropdownToggle('mode')}
            aria-label="Changer de mode"
            aria-expanded={modeOpen}
          >
            <span className={styles.ipLabel}>{activeMode.label}</span>
            <Icon icon="chevron" size="pico" rotate={modeOpen ? 180 : 0} color="currentColor" />
          </button>
        </div>

        <div className={styles.barSep} />

        {/* Mic button */}
        <button
          className={styles.iconZone}
          onMouseDown={(e) => e.nativeEvent.stopPropagation()}
          onClick={() => onDropdownToggle('mic')}
          title={activeMic?.label ?? 'Microphone'}
        >
          <Icon icon="mic" size="micro" />
        </button>

        <div className={styles.barSep} />

        {/* Settings */}
        <button
          className={styles.iconZone}
          onClick={onSettingsClick}
          title="Paramètres"
          aria-label="Paramètres"
        >
          <Icon icon="param" size="micro" />
        </button>

        <div className={styles.barSep} />

        {/* Info — voice commands */}
        <button
          className={styles.iconZone}
          onMouseDown={(e) => e.nativeEvent.stopPropagation()}
          onClick={() => setInfoOpen((v) => !v)}
          title="Commandes vocales"
          aria-label="Commandes vocales"
          aria-expanded={infoOpen}
        >
          <Icon icon="infoCircle" size="micro" color="currentColor" />
        </button>
      </div>

      {/* Popovers — anchored to barZone, not clipped by bar's overflow:hidden */}
      {modeOpen && (
        <ModeSelector
          modes={availableModes}
          activeModeId={mode}
          direction={panelDirection}
          onSelect={onModeChange}
          onClose={() => setModeOpen(false)}
        />
      )}

      {micOpen && (
        <div className={styles.popAnchorMic}>
          <WidgetPopover onClose={() => setMicOpen(false)} align="right" direction={panelDirection}>
            <div className={styles.popTitle}>Microphone</div>
            {devices.map((d) => {
              const isActive = d.deviceId === selectedDeviceId
              return (
                <button
                  key={d.deviceId}
                  className={classNames(styles.popRow, { [styles.popRowActive]: isActive })}
                  onClick={() => onMicSelect(d.deviceId)}
                >
                  <span className={styles.popRowMic}>
                    {d.label || `Microphone ${d.deviceId.slice(0, 8)}`}
                  </span>
                  {isActive && <Icon icon="check" size="pico" color="currentColor" />}
                </button>
              )
            })}
          </WidgetPopover>
        </div>
      )}

      {infoOpen && (
        <div className={styles.popAnchorInfo}>
          <WidgetPopover
            onClose={() => setInfoOpen(false)}
            align="right"
            direction={panelDirection}
          >
            <div className={styles.popTitle}>Commandes vocales</div>
            <div className={styles.popInfo}>
              <div className={styles.popInfoLabel}>"Supprime la saisie"</div>
              <div className={styles.popInfoHint}>Efface la dernière phrase dictée</div>
            </div>
            <div className={styles.popInfo}>
              <div className={styles.popInfoLabel}>"Supprime le contenu"</div>
              <div className={styles.popInfoHint}>Efface tout le contenu</div>
            </div>
          </WidgetPopover>
        </div>
      )}
    </div>
  )
}
